import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import FileUpload from '../ui/custom/FileUpload';
import AttachmentPreview from './AttachmentPreview';
import { CommentFormData } from '../../types/comment';
import FilePreview from './FilePreview';

interface TicketCommentFormProps {
  onCommentSubmit: (data: CommentFormData, onUploadProgress: (progress: number) => void) => Promise<void>;
  isSubmitting?: boolean;
  ticketId?: number | string; // Add ticketId prop to support direct API calls
}

export default function TicketCommentForm({ onCommentSubmit, isSubmitting = false, ticketId }: TicketCommentFormProps) {
  const { register, handleSubmit, reset, formState: { errors }, watch, setValue } = useForm<CommentFormData>();
  const [isFocused, setIsFocused] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  
  const content = watch('content');
  const hasContent = content && content.trim().length > 0;

  const handleFormSubmit = async (data: CommentFormData) => {
    try {
      const progress: Record<string, number> = {};
      data.attachments?.forEach(file => {
        progress[file.name] = 0;
      });
      setUploadProgress(progress);

      await onCommentSubmit(data, (fileProgress) => {
        // Update progress for all files with the same progress value
        const newProgress: Record<string, number> = {};
        data.attachments?.forEach(file => {
          newProgress[file.name] = fileProgress;
        });
        setUploadProgress(prev => ({ ...prev, ...newProgress }));
      });
      // Reset the form only after successful submission
      reset();
      setUploadProgress({});
    } catch (error) {
      console.error('Failed to submit comment:', error);
    }
  };

  return (
    <div className={`mt-6 p-3 sm:p-4 rounded-2xl border transition-all duration-200 ${
      isFocused
        ? theme === 'dark'
          ? 'bg-gray-800/90 border-blue-500/50 shadow-lg shadow-blue-900/20'
          : 'bg-white border-blue-300/60 shadow-lg shadow-blue-200/30'
        : theme === 'dark'
        ? 'bg-gray-800/60 border-gray-700/40 shadow-md'
        : 'bg-white border-gray-200/60 shadow-md'
    }`}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div className="relative">
          <label htmlFor="content" className={`block text-sm font-semibold mb-2 ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
          }`}>
            {t('tickets.addComment', 'Add Comment')}
          </label>
          
          <div className="relative">
            <textarea
              {...register('content', { 
                required: t('tickets.commentRequired', 'Please enter a comment'),
                minLength: { value: 3, message: t('tickets.commentMinLength', 'Comment must be at least 3 characters') }
              })}
              id="content"
              rows={4}
              disabled={isSubmitting}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={`
                block w-full rounded-xl border px-4 py-3 text-sm
                ${errors.content
                  ? theme === 'dark'
                    ? 'border-red-500/50 bg-red-900/20 text-red-100 placeholder-red-400/50 focus:border-red-400 focus:ring-red-400/30 focus:ring-offset-gray-800'
                    : 'border-red-300 bg-red-50 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500/30 focus:ring-offset-white'
                  : theme === 'dark'
                    ? 'border-gray-600/50 bg-gray-900/60 text-gray-100 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/30 focus:ring-offset-gray-800'
                    : 'border-gray-300 bg-gray-50 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500/30 focus:ring-offset-white'
                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
              placeholder={t('tickets.commentPlaceholder', 'Type your comment here...')}
            />
          </div>
          
          {errors.content && (
            <div className={`mt-2 flex items-center gap-2 text-sm ${
              theme === 'dark' ? 'text-red-400' : 'text-red-600'
            }`}>
              <div className="w-1 h-1 rounded-full bg-current"></div>
              {errors.content.message}
            </div>
          )}
        </div>

        {/* Attachment section */}
        <FileUpload
          onChange={(files) => {
            setValue('attachments', files);
            setUploadProgress({});
          }}
          value={watch('attachments')}
        />

        {/* Upload progress indicators */}
        {Object.entries(uploadProgress).map(([fileName, progress]) => (
          <div key={fileName} className="mt-2">
            <div className="flex justify-between text-sm">
              <span className={`truncate max-w-[200px] ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                {fileName}
              </span>
              <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {Math.round(progress)}%
              </span>
            </div>
            <div className={`w-full h-1.5 rounded-full overflow-hidden mt-1 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            }`}>
              <div
                className={`h-full ${
                  progress < 100 
                    ? 'bg-blue-500' 
                    : 'bg-green-500'
                } transition-all duration-300`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ))}

        {/* Image previews for attachments */}
        {(watch('attachments') || []).length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {(watch('attachments') || []).map((file, index) => (
              <FilePreview
                key={`${file.name}-${index}`}
                file={file}
                onRemove={() => {
                  const currentFiles = watch('attachments') || [];
                  const newFiles = [...currentFiles];
                  newFiles.splice(index, 1);
                  setValue('attachments', newFiles);
                }}
              />
            ))}
          </div>
        )}

        {/* Form actions */}
        <div className="flex justify-end pt-2">
          <div className="flex items-center gap-3">
            {/* Cancel button (optional) */}
            {hasContent && (
              <button
                type="button"
                onClick={() => reset()}
                className={`px-4 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  theme === 'dark'
                    ? 'text-gray-200 bg-gray-700 border-gray-600 hover:bg-gray-600'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
            )}
            
            {/* Submit button */}
            <button
              type="submit"
              disabled={isSubmitting || !hasContent}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {t('tickets.adding', 'Adding...')}
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-4 h-4" />&nbsp;
                  {t('tickets.addComment', 'Add Comment')}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}